import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { sortFieldValuesForDisplay } from "./detailsDialogUtils";

function formatFieldValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Included" : "Not Included";
  return String(value);
}

function isPdfAssetUrl(url) {
  return /\.pdf([?#].*)?$/i.test(String(url || "").trim());
}

function buildPdfEmbedUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  const hasHash = raw.includes("#");
  return `${raw}${hasHash ? "&" : "#"}toolbar=0&navpanes=0&scrollbar=0`;
}

function getCardMedia(entry) {
  const images = Array.isArray(entry?.images) ? entry.images : [];
  if (images.length === 0) return { url: "", isPdf: false };

  const coverImage = images.find(
    (image) =>
      String(image?.type || "")
        .trim()
        .toLowerCase() === "cover" && image?.url
  );
  if (coverImage?.url) {
    return {
      url: coverImage.url,
      previewUrl: coverImage.previewUrl || "",
      isPdf: isPdfAssetUrl(coverImage.url),
    };
  }

  const fallbackUrl = images[0]?.url || "";
  return {
    url: fallbackUrl,
    previewUrl: images[0]?.previewUrl || "",
    isPdf: isPdfAssetUrl(fallbackUrl),
  };
}

function Cards(props) {
  const { entries, loading, loadingMore, error, hasMore, onLoadMore, onOpenDetails } =
    props;
  const scrollAreaRef = useRef(null);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    if (!hasMore || loading || loadingMore || typeof onLoadMore !== "function") {
      return undefined;
    }

    const sentinel = loadMoreRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (observerEntries) => {
        if (observerEntries[0]?.isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: scrollAreaRef.current,
        rootMargin: "220px 0px",
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore, entries.length]);

  const maxGalleryFieldCount = useMemo(
    () =>
      entries.reduce((maxCount, entry) => {
        const count = (entry.fieldValues || []).filter(
          (fieldValue) => fieldValue?.fieldId?.showOnGallery
        ).length;
        return Math.max(maxCount, count);
      }, 0),
    [entries]
  );

  const galleryCards = useMemo(
    () =>
      entries.map((entry) => {
        const galleryFields = sortFieldValuesForDisplay(
          entry.fieldValues || []
        ).filter((fieldValue) => fieldValue?.fieldId?.showOnGallery);
        const media = getCardMedia(entry);
        const hasGalleryFields = galleryFields.length > 0;
        const placeholderCount = Math.max(
          0,
          maxGalleryFieldCount - galleryFields.length
        );

        return (
          <Box className="browse-designs-card" key={entry._id}>
            <Typography className="browse-designs-title">{entry.name}</Typography>

            <Box className="browse-designs-image-wrap">
              {media.url && !media.isPdf ? (
                <img
                  src={media.url}
                  alt={entry.name}
                  className="browse-designs-image"
                />
              ) : media.url && media.isPdf && media.previewUrl ? (
                <img
                  src={media.previewUrl}
                  alt={`${entry.name} PDF preview`}
                  className="browse-designs-image browse-designs-image-pdf-preview"
                />
              ) : media.url && media.isPdf ? (
                <object
                  data={buildPdfEmbedUrl(media.url)}
                  type="application/pdf"
                  className="browse-designs-pdf-object"
                >
                  <Box className="browse-designs-pdf-placeholder">PDF Preview</Box>
                </object>
              ) : (
                <Box className="browse-designs-image-placeholder">No Image</Box>
              )}
            </Box>

            <Box
              className="browse-designs-fields"
              sx={{
                borderTop: hasGalleryFields ? "1px solid #c9c9c9" : "none",
                paddingTop: hasGalleryFields ? "8px" : 0,
              }}
            >
              {galleryFields.map((fieldValue, idx) => (
                <Box
                  className="browse-designs-field-row"
                  key={`${entry._id}-field-${idx}`}
                >
                  <span>{fieldValue.fieldId?.name}</span>
                  <span>{formatFieldValue(fieldValue.value)}</span>
                </Box>
              ))}
              {Array.from({ length: placeholderCount }).map((_, idx) => (
                <Box
                  className="browse-designs-field-row"
                  key={`${entry._id}-field-placeholder-${idx}`}
                  sx={{ visibility: "hidden", backgroundImage: "none" }}
                >
                  <span>-</span>
                  <span>-</span>
                </Box>
              ))}
            </Box>

            <Button
              variant="contained"
              size="small"
              className="browse-designs-details-btn"
              onClick={() => onOpenDetails(entry)}
              endIcon={<KeyboardArrowDownIcon fontSize="small" />}
            >
              Details
            </Button>
          </Box>
        );
      }),
    [entries, maxGalleryFieldCount, onOpenDetails]
  );

  return (
    <Box className="browse-designs-scroll-area" ref={scrollAreaRef}>
      {loading ? (
        <Box className="browse-designs-state-wrap">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box className="browse-designs-state-wrap">
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <Box className="browse-designs-grid">
          {galleryCards}
          {!hasMore && entries.length > 0 && (
            <Box
              className="browse-designs-card"
              sx={{
                gridColumn: "1 / -1",
                justifySelf: "center",
                width: "auto",
                minWidth: 0,
                px: 3,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderStyle: "dashed",
                gap: 0,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Alegreya Sans SC", "Trebuchet MS", sans-serif',
                  textTransform: "uppercase",
                  letterSpacing: "1.2px",
                  color: "#5f6f86",
                  textAlign: "center",
                  px: 2,
                }}
              >
                End of Results
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {!loading && hasMore && (
        <Box ref={loadMoreRef} sx={{ height: 1, width: "100%" }} />
      )}

      {loadingMore && (
        <Box className="browse-designs-state-wrap" sx={{ py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
}

export default Cards;
